import { Box } from '@ltvco/refresh-lib/theme';
import {
  KeyboardEvent,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { AutosuggestionPrompts } from 'components/DeepSearch/components/AutosuggestionPrompts';
import { ClickHereTooltip } from 'components/DeepSearch/components/ClickHereTooltip';
import {
  AnimatedInputButtonContainer,
  AnimatedInputContainer,
  AnimatedSendButton,
  ArrowUpwardIcon,
  SendButton,
  GhostInputRow,
  GhostMirror,
  GhostText,
} from 'components/DeepSearch/components/components';
import { PLACEHOLDER_COLLECTION } from 'components/DeepSearch/constants';

import { DeepSearchContext } from '../context';

import {
  AnimatedDisclaimer,
  DeepSearchInputTextarea,
  SendButtonRow,
} from './DeepSearchChatInput.styles';
import {
  DragDropOverlay,
  FilePreview,
  FileUploadButton,
  FileUploadErrorAlert,
  useFileUpload,
} from './FileUpload';

const GHOST_CARET_BUFFER = 3;

export interface DeepSearchChatInputRef {
  setValue: (value: string, ghost?: string) => void;
}

interface DeepSearchChatInputProps {
  isLanding?: boolean;
  // When true the disclaimer starts invisible and fades in — gives the chat-view
  // input a soft entrance when it appears after the landing slides off-screen.
  initiallyHidden?: boolean;
}

export const DeepSearchChatInput = forwardRef<
  DeepSearchChatInputRef,
  DeepSearchChatInputProps
>(({ isLanding, initiallyHidden }, ref) => {
  const { sendMessage, isLoading } = useContext(DeepSearchContext);
  const [inputValue, setInputValue] = useState('');
  const [ghostText, setGhostText] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputElementRef = useRef<HTMLTextAreaElement>(null);
  const [disclaimerVisible, setDisclaimerVisible] = useState(!initiallyHidden);
  const {
    selectedFile,
    previewUrl,
    kind,
    error: uploadError,
    isDragging,
    handleFileSelect,
    handleRemoveFile,
    setError: setUploadError,
  } = useFileUpload();

  useEffect(() => {
    if (initiallyHidden) {
      const timer = setTimeout(() => setDisclaimerVisible(true), 300);
      return () => clearTimeout(timer);
    }
  }, []);
  const mirrorRef = useRef<HTMLSpanElement>(null);

  const hasInputValue = inputValue.trim().length > 0;
  const hasSelectedFile = Boolean(selectedFile);
  const canSend = (hasInputValue || hasSelectedFile) && !isLoading;
  const showGhost = ghostText.length > 0;

  useLayoutEffect(() => {
    const textarea = inputElementRef.current;
    if (!textarea) {
      return;
    }
    textarea.style.width =
      showGhost && mirrorRef.current
        ? `${mirrorRef.current.offsetWidth + GHOST_CARET_BUFFER}px`
        : '100%';
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  }, [showGhost, inputValue]);

  useImperativeHandle(ref, () => ({
    setValue: (value: string, ghost?: string) => {
      setInputValue(value);
      setGhostText(ghost ?? '');
      requestAnimationFrame(() => {
        const textarea = inputElementRef.current;
        if (!textarea) {
          return;
        }
        textarea.focus();
        textarea.setSelectionRange(value.length, value.length);
      });
    },
  }));

  const handleSend = () => {
    if (!canSend) {
      return;
    }
    sendMessage(inputValue.trim(), undefined, selectedFile ?? undefined);
    handleRemoveFile();
    if (!isLanding) {
      setInputValue('');
      setGhostText('');
      if (inputElementRef.current) {
        inputElementRef.current.style.height = 'auto';
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (ghostText) {
      setGhostText('');
    }
    // Empty-input-only rule: the suggestions panel is a starting-prompt helper,
    // so it closes the moment the user begins typing.
    if (showSuggestions) {
      setShowSuggestions(false);
    }
    setInputValue(e.target.value);
  };

  // Landing only: open the suggestions panel when the field is empty. Wired to both
  // focus (tab/programmatic) and click so it reopens on a repeat click of an
  // already-focused input — focus alone doesn't re-fire in that case.
  const maybeOpenSuggestions = () => {
    if (isLanding && inputValue.trim() === '') {
      setShowSuggestions(true);
    }
  };

  const handleSelectPrompt = (
    suggestionMainText: string,
    suggestionExampleText: string
  ) => {
    setInputValue(suggestionMainText);
    setGhostText(suggestionExampleText);
    setShowSuggestions(false);
    requestAnimationFrame(() => {
      const textarea = inputElementRef.current;
      if (!textarea) {
        return;
      }
      textarea.focus();
      textarea.setSelectionRange(
        suggestionMainText.length,
        suggestionMainText.length
      );
    });
  };

  const focusInputAtEnd = () => {
    const textarea = inputElementRef.current;
    if (!textarea) {
      return;
    }
    textarea.focus();
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
  };

  const handleRowMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target !== inputElementRef.current) {
      e.preventDefault();
      focusInputAtEnd();
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && canSend) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <AnimatedInputContainer
      data-role={isLanding ? 'landing-chat-input' : 'chat-input'}
      isLanding={isLanding}
    >
      <AnimatedInputButtonContainer
        isLanding={isLanding}
        showSuggestions={showSuggestions}
      >
        {selectedFile && kind && (
          <Box sx={{ px: 2, pt: 1, alignSelf: 'flex-start' }}>
            <FilePreview
              kind={kind}
              previewUrl={previewUrl}
              fileName={selectedFile.name}
              onRemove={handleRemoveFile}
            />
          </Box>
        )}
        <GhostInputRow isLanding={isLanding} onMouseDown={handleRowMouseDown}>
          <DeepSearchInputTextarea
            ref={inputElementRef}
            placeholder="What can I help you find?"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={maybeOpenSuggestions}
            onClick={maybeOpenSuggestions}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            rows={1}
            sx={{
              boxSizing: 'border-box',
              flex: showGhost ? 'none' : undefined,
              ...(showGhost && { paddingRight: 0, whiteSpace: 'nowrap' }),
            }}
          />
          {showGhost && <GhostText>{ghostText}</GhostText>}
          {showGhost && (
            <GhostMirror ref={mirrorRef} aria-hidden="true">
              {inputValue}
            </GhostMirror>
          )}
        </GhostInputRow>
        <SendButtonRow isLanding={isLanding}>
          <FileUploadButton
            onFileSelect={handleFileSelect}
            onError={setUploadError}
            disabled={isLoading}
          />
          <AnimatedSendButton>
            <SendButton
              onClick={handleSend}
              hasInputValue={canSend}
              disabled={!canSend}
            >
              <ArrowUpwardIcon hasInputValue={canSend} />
            </SendButton>
          </AnimatedSendButton>
        </SendButtonRow>
      </AnimatedInputButtonContainer>
      {uploadError && <FileUploadErrorAlert message={uploadError} />}
      <DragDropOverlay isVisible={isDragging} />
      {isLanding && (
        <AutosuggestionPrompts
          isVisible={showSuggestions}
          items={PLACEHOLDER_COLLECTION}
          scrollIntoViewOnOpen
          onSelectPrompt={handleSelectPrompt}
          onClose={() => setShowSuggestions(false)}
        />
      )}
      {!isLanding && (
        <AnimatedDisclaimer component="span" visible={disclaimerVisible}>
          This chat feature is powered by artificial intelligence.{' '}
          <ClickHereTooltip>Click here</ClickHereTooltip> to view our AI usage
          disclaimer
        </AnimatedDisclaimer>
      )}
    </AnimatedInputContainer>
  );
});
